import React, { useState, useEffect, useCallback, useRef } from 'react';
import { SpecialitySelect } from '../components/ui/SpecialitySelect';
import { CityStateSelect } from '../components/ui/CityStateSelect';
import { AppLayout } from '../components/layout/AppLayout';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';

const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

const COUNTRIES = [
  { name: 'India', code: 'IN', dial: '+91', flag: '🇮🇳' },
  { name: 'Afghanistan', code: 'AF', dial: '+93', flag: '🇦🇫' },
  { name: 'Australia', code: 'AU', dial: '+61', flag: '🇦🇺' },
  { name: 'Bahrain', code: 'BH', dial: '+973', flag: '🇧🇭' },
  { name: 'Bangladesh', code: 'BD', dial: '+880', flag: '🇧🇩' },
  { name: 'Bhutan', code: 'BT', dial: '+975', flag: '🇧🇹' },
  { name: 'Canada', code: 'CA', dial: '+1', flag: '🇨🇦' },
  { name: 'China', code: 'CN', dial: '+86', flag: '🇨🇳' },
  { name: 'Egypt', code: 'EG', dial: '+20', flag: '🇪🇬' },
  { name: 'France', code: 'FR', dial: '+33', flag: '🇫🇷' },
  { name: 'Germany', code: 'DE', dial: '+49', flag: '🇩🇪' },
  { name: 'Hong Kong', code: 'HK', dial: '+852', flag: '🇭🇰' },
  { name: 'Indonesia', code: 'ID', dial: '+62', flag: '🇮🇩' },
  { name: 'Iran', code: 'IR', dial: '+98', flag: '🇮🇷' },
  { name: 'Israel', code: 'IL', dial: '+972', flag: '🇮🇱' },
  { name: 'Italy', code: 'IT', dial: '+39', flag: '🇮🇹' },
  { name: 'Japan', code: 'JP', dial: '+81', flag: '🇯🇵' },
  { name: 'Jordan', code: 'JO', dial: '+962', flag: '🇯🇴' },
  { name: 'Kenya', code: 'KE', dial: '+254', flag: '🇰🇪' },
  { name: 'Kuwait', code: 'KW', dial: '+965', flag: '🇰🇼' },
  { name: 'Malaysia', code: 'MY', dial: '+60', flag: '🇲🇾' },
  { name: 'Maldives', code: 'MV', dial: '+960', flag: '🇲🇻' },
  { name: 'Myanmar', code: 'MM', dial: '+95', flag: '🇲🇲' },
  { name: 'Nepal', code: 'NP', dial: '+977', flag: '🇳🇵' },
  { name: 'Netherlands', code: 'NL', dial: '+31', flag: '🇳🇱' },
  { name: 'New Zealand', code: 'NZ', dial: '+64', flag: '🇳🇿' },
  { name: 'Nigeria', code: 'NG', dial: '+234', flag: '🇳🇬' },
  { name: 'Oman', code: 'OM', dial: '+968', flag: '🇴🇲' },
  { name: 'Pakistan', code: 'PK', dial: '+92', flag: '🇵🇰' },
  { name: 'Philippines', code: 'PH', dial: '+63', flag: '🇵🇭' },
  { name: 'Qatar', code: 'QA', dial: '+974', flag: '🇶🇦' },
  { name: 'Russia', code: 'RU', dial: '+7', flag: '🇷🇺' },
  { name: 'Saudi Arabia', code: 'SA', dial: '+966', flag: '🇸🇦' },
  { name: 'Singapore', code: 'SG', dial: '+65', flag: '🇸🇬' },
  { name: 'South Africa', code: 'ZA', dial: '+27', flag: '🇿🇦' },
  { name: 'South Korea', code: 'KR', dial: '+82', flag: '🇰🇷' },
  { name: 'Sri Lanka', code: 'LK', dial: '+94', flag: '🇱🇰' },
  { name: 'Taiwan', code: 'TW', dial: '+886', flag: '🇹🇼' },
  { name: 'Thailand', code: 'TH', dial: '+66', flag: '🇹🇭' },
  { name: 'Turkey', code: 'TR', dial: '+90', flag: '🇹🇷' },
  { name: 'United Arab Emirates', code: 'AE', dial: '+971', flag: '🇦🇪' },
  { name: 'United Kingdom', code: 'GB', dial: '+44', flag: '🇬🇧' },
  { name: 'United States', code: 'US', dial: '+1', flag: '🇺🇸' },
  { name: 'Vietnam', code: 'VN', dial: '+84', flag: '🇻🇳' },
  { name: 'Other', code: 'OT', dial: '+', flag: '🌍' },
];

function CountrySelector({ value, onChange }) {
  const [countryQuery, setCountryQuery] = useState(value || 'India');
  const [countryOpen, setCountryOpen] = useState(false);
  const countryRef = useRef(null);

  useEffect(() => { setCountryQuery(value || 'India'); }, [value]);

  useEffect(() => {
    const handler = (e) => { if (countryRef.current && !countryRef.current.contains(e.target)) setCountryOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selectedCountry = COUNTRIES.find(c => c.name === value) || COUNTRIES[0];
  const filteredCountries = COUNTRIES.filter(c => c.name.toLowerCase().includes(countryQuery.toLowerCase()));

  const pickCountry = (c) => {
    onChange(c.name);
    setCountryQuery(c.name);
    setCountryOpen(false);
  };

  return (
    <div ref={countryRef} style={{ position: 'relative' }}>
      <label className="form-label">Country</label>
      <div style={{ position: 'relative' }}>
        <input
          className="form-control"
          style={{ paddingLeft: 36, paddingRight: 32 }}
          value={countryQuery}
          onChange={e => { setCountryQuery(e.target.value); setCountryOpen(true); }}
          onFocus={() => setCountryOpen(true)}
          placeholder="Search country…"
          autoComplete="off"
        />
        <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: '1rem', pointerEvents: 'none' }}>{selectedCountry.flag}</span>
        <svg style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
      </div>
      {countryOpen && filteredCountries.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 400, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', maxHeight: 220, overflowY: 'auto', boxShadow: 'var(--shadow-lg)', marginTop: 2 }}>
          {filteredCountries.map(c => (
            <div key={c.code} onMouseDown={() => pickCountry(c)}
              style={{ padding: '9px 12px', cursor: 'pointer', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: 10,
                background: c.name === value ? 'var(--brand-purple-faint)' : 'transparent',
                color: c.name === value ? 'var(--brand-purple)' : 'var(--text-primary)' }}
              onMouseEnter={e => { if (c.name !== value) e.currentTarget.style.background = 'var(--bg-hover, #F9FAFB)'; }}
              onMouseLeave={e => { if (c.name !== value) e.currentTarget.style.background = 'transparent'; }}>
              <span style={{ fontSize: '1.1rem', width: 22, textAlign: 'center' }}>{c.flag}</span>
              <span>{c.name}</span>
              <span style={{ marginLeft: 'auto', color: 'var(--text-muted)', fontSize: '0.78rem' }}>{c.dial}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const fmt = n => { if (!n||n===0) return '₹0'; if (n>=10000000) return `₹${(n/10000000).toFixed(1)}Cr`; if (n>=100000) return `₹${(n/100000).toFixed(1)}L`; if (n>=1000) return `₹${(n/1000).toFixed(0)}K`; return `₹${Math.round(n)}`; };

const TYPE_CONFIG = {
  'Distributor':         { icon:'🏭', color:'#D97706', bg:'#FEF3C7' },
  'Key Hospital':        { icon:'🏥', color:'#0369A1', bg:'#E0F2FE' },
  'Medical College':     { icon:'🎓', color:'#7C3AED', bg:'#EDE9FE' },
  'KOL':                 { icon:'🌟', color:'#7C3AED', bg:'#EDE9FE' },
  'Diagnostic Lab':      { icon:'🔬', color:'#059669', bg:'#D1FAE5' },
  'Corporate / OHC':     { icon:'🏢', color:'#6B7280', bg:'#F3F4F6' },
  'Pharmacy Chain':      { icon:'💊', color:'#DC2626', bg:'#FEE2E2' },
  'Academic / Research': { icon:'📚', color:'#0F766E', bg:'#CCFBF1' },
  'Government / PSU':    { icon:'🏛️', color:'#4338CA', bg:'#E0E7FF' },
  'Individual':          { icon:'👤', color:'#BE185D', bg:'#FCE7F3' },
};

const JOURNEY_STAGES = ['prospect','contacted','demo_scheduled','demo_done','evaluating','converted','recurring','kol_advocate','dormant'];
const JOURNEY_LABELS = { prospect:'Prospect', contacted:'Contacted', demo_scheduled:'Demo Scheduled', demo_done:'Demo Done', evaluating:'Evaluating', converted:'Converted', recurring:'Recurring', kol_advocate:'KOL / Advocate', dormant:'Dormant' };

const getTypeConfig = t => TYPE_CONFIG[t] || { icon:'🏢', color:'#6B7280', bg:'#F3F4F6' };

function SearchSelect({ value, onChange, options=[], placeholder, style={} }) {
  const [open,setOpen]=useState(false); const [q,setQ]=useState(''); const ref=useRef(null);
  useEffect(()=>{ const h=(e)=>{ if(ref.current&&!ref.current.contains(e.target))setOpen(false); }; document.addEventListener('mousedown',h); return()=>document.removeEventListener('mousedown',h); },[]);
  const filtered=options.filter(o=>(o.label||o.value||o).toLowerCase().includes(q.toLowerCase()));
  const select=(val)=>{onChange(val);setQ('');setOpen(false);};
  const disp=options.find(o=>(o.value||o)===value);
  const dispLabel=disp?(disp.label||disp.value||disp):(value||'');
  return (
    <div ref={ref} style={{position:'relative',...style}}>
      <div className="form-control" style={{display:'flex',alignItems:'center',gap:6,cursor:'pointer',userSelect:'none',minWidth:0}} onClick={()=>setOpen(o=>!o)}>
        <span style={{flex:1,color:dispLabel?'var(--text-primary)':'var(--text-muted)',fontSize:'0.875rem',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{dispLabel||placeholder||'Select…'}</span>
        {value&&<span onClick={e=>{e.stopPropagation();onChange('');setQ('');}} style={{color:'var(--text-muted)',fontSize:11,cursor:'pointer',flexShrink:0}}>✕</span>}
        <span style={{color:'var(--text-muted)',fontSize:9,flexShrink:0}}>{open?'▲':'▼'}</span>
      </div>
      {open&&(
        <div style={{position:'absolute',top:'calc(100% + 4px)',left:0,right:0,zIndex:300,background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:'var(--radius-md)',boxShadow:'var(--shadow-lg)',maxHeight:240,display:'flex',flexDirection:'column'}}>
          <div style={{padding:'5px 7px',borderBottom:'1px solid var(--border)'}}><input autoFocus className="form-control" style={{padding:'5px 9px',fontSize:'0.82rem'}} placeholder="Type to search…" value={q} onChange={e=>setQ(e.target.value)} onClick={e=>e.stopPropagation()}/></div>
          <div style={{overflowY:'auto'}}>
            {filtered.length===0?<div style={{padding:'12px 14px',color:'var(--text-muted)',fontSize:'0.82rem',textAlign:'center'}}>No results</div>
              :filtered.map(opt=>{const v=opt.value||opt,l=opt.label||opt.value||opt; return <div key={v} onClick={()=>select(v)} style={{padding:'9px 14px',cursor:'pointer',fontSize:'0.875rem',borderBottom:'1px solid var(--border)',background:v===value?'var(--brand-purple-faint)':'transparent',color:v===value?'var(--brand-purple)':'var(--text-primary)',fontWeight:v===value?600:400}}>{l}</div>;})}
          </div>
        </div>
      )}
    </div>
  );
}

// Searchable combobox for district — shows territory-scoped options, allows free typing
function DistrictCombobox({ value, onChange, options = [], placeholder = 'Search or type district…' }) {
  const [query, setQuery] = useState(value || '');
  const [open, setOpen]   = useState(false);
  const ref               = useRef(null);

  useEffect(() => { if (value !== query) setQuery(value || ''); }, [value]); // eslint-disable-line

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = query.trim()
    ? options.filter(o => o.toLowerCase().includes(query.toLowerCase()))
    : options;

  const select = (city) => { onChange(city); setQuery(city); setOpen(false); };
  const clear  = (e)    => { e.stopPropagation(); onChange(''); setQuery(''); };

  return (
    <div style={{ position:'relative' }} ref={ref}>
      <input
        className="form-control"
        value={query}
        placeholder={placeholder}
        autoComplete="off"
        onChange={e => { setQuery(e.target.value); onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
      />
      {query && (
        <span onClick={clear} style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', cursor:'pointer', color:'var(--text-muted)', fontSize:11 }}>✕</span>
      )}
      {open && (filtered.length > 0 || query.trim()) && (
        <div style={{ position:'absolute', top:'100%', left:0, right:0, zIndex:400, background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'var(--radius-md)', boxShadow:'var(--shadow-lg)', marginTop:3, maxHeight:220, overflowY:'auto' }}>
          {filtered.length === 0 && (
            <div style={{ padding:'10px 14px', fontSize:'0.82rem', color:'var(--text-muted)' }}>No matches — "{query.trim()}" will be used as-is</div>
          )}
          {filtered.map(city => (
            <div key={city} onMouseDown={() => select(city)}
              style={{ padding:'8px 14px', cursor:'pointer', fontSize:'0.875rem', borderBottom:'1px solid var(--border)', background: city===value ? 'var(--brand-purple-faint)' : 'transparent', color: city===value ? 'var(--brand-purple)' : 'var(--text-primary)', fontWeight: city===value ? 600 : 400 }}
              onMouseEnter={e => { if (city!==value) e.currentTarget.style.background='var(--bg)'; }}
              onMouseLeave={e => { if (city!==value) e.currentTarget.style.background='transparent'; }}>
              {city}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

async function deleteAccount(id, name, reload) {
  if (!window.confirm(`Delete account "${name}"?\nThis can be restored by an admin.`)) return;
  try { await api.delete(`/accounts/${id}`); toast.success('Account deleted'); reload(); }
  catch (e) { toast.error(e.response?.data?.message || 'Delete failed'); }
}

export default function AccountsPage() {
  const { user } = useAuth();
  const [accounts, setAccounts]     = useState([]);
  const [stats, setStats]           = useState(null);
  const [loading, setLoading]       = useState(true);
  const [page, setPage]             = useState(1);
  const [meta, setMeta]             = useState({ total:0, limit:20 });
  const [search, setSearch]         = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [priorityFilter, setPriority] = useState('');
  const [districtFilter, setDistrictFilter] = useState('');
  const [stateFilter, setStateFilter]     = useState('');
  const [districts, setDistricts]         = useState([]);
  const [states, setStates]               = useState([]);
  const [selectedAccount, setSelected] = useState(null);
  const _aq = new URLSearchParams(window.location.search);
  const _prefillAccount = _aq.get('new')==='1' ? { name:_aq.get('name')||'', phone:_aq.get('phone')||'', city:_aq.get('city')||'', territory_id:_aq.get('territory_id')||'' } : null;
  const [showAdd, setShowAdd]       = useState(_aq.get('new')==='1' ? 'Key Hospital' : null); // account_type or null
  const [editAccount, setEdit]      = useState(null);
  const [logActivityFor, setLogActivityFor] = useState(null); // { id, name }

  const load = useCallback(() => {
    setLoading(true);
    const params = { page, limit:20 };
    if (search)         params.search = search;
    if (typeFilter)     params.account_type = typeFilter;
    if (priorityFilter) params.priority = priorityFilter;
    if (districtFilter) params.district = districtFilter;
    if (stateFilter)    params.state = stateFilter;
    api.get('/accounts', { params })
      .then(r => { setAccounts(r.data.data); setMeta(r.data.meta); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, search, typeFilter, priorityFilter, districtFilter, stateFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/accounts/stats').then(r => setStats(r.data.data)).catch(()=>{});
  }, []);
  useEffect(() => {
    api.get('/master-data/state').then(r => setStates(r.data.data || [])).catch(()=>{});
  }, []);
  useEffect(() => {
    const params = stateFilter ? { state: stateFilter } : {};
    api.get('/accounts/districts', { params }).then(r => setDistricts(r.data.data || [])).catch(()=>{});
  }, [stateFilter]);

  const TYPES = Object.keys(TYPE_CONFIG);

  const exportAccounts = async () => {
    try {
      const params = { limit:10000 };
      if (typeFilter)     params.account_type = typeFilter;
      if (priorityFilter) params.priority = priorityFilter;
      if (search)         params.search = search;
      if (districtFilter) params.district = districtFilter;
      if (stateFilter)    params.state = stateFilter;
      const r = await api.get('/accounts', { params });
      const rows = r.data.data;
      const header = 'Name,Type,Priority,Territory,District,Journey Stage,LTV Annual,Contact';
      const csv = [header, ...rows.map(a => [
        `"${(a.name||'').replace(/"/g,'""')}"`, a.account_type||'', a.priority||'',
        `"${a.territory_name||''}"`, `"${a.district||''}"`, a.journey_stage||'',
        a.ltv_annual||0, a.contact_number||''
      ].join(','))].join('\n');
      const el = document.createElement('a');
      el.href = URL.createObjectURL(new Blob([csv], { type:'text/csv' }));
      el.download = `accounts_export_${new Date().toISOString().slice(0,10)}.csv`;
      el.click();
    } catch { toast.error('Export failed'); }
  };

  return (
    <AppLayout title="Accounts" subtitle="All accounts — doctors, distributors, KOLs, hospitals and more">
      {/* Stats bar */}
      {stats && (
        <div style={{ display:'flex', gap:8, marginBottom:14, flexWrap:'wrap' }}>
          <div style={{ padding:'8px 14px', borderRadius:8, background:'var(--bg-card)', border:'1px solid var(--border)', fontSize:'0.82rem' }}>
            <span style={{ color:'var(--text-muted)' }}>Total </span>
            <strong>{Number(stats.totals?.total||0).toLocaleString()}</strong>
          </div>
          <div style={{ padding:'8px 14px', borderRadius:8, background:'#EEEDFE', border:'1px solid #403EC9', fontSize:'0.82rem', color:'#403EC9' }}>
            <strong>A: {stats.totals?.a||0}</strong>
          </div>
          <div style={{ padding:'8px 14px', borderRadius:8, background:'var(--bg-card)', border:'1px solid var(--border)', fontSize:'0.82rem' }}>
            B: {stats.totals?.b||0}
          </div>
          <div style={{ padding:'8px 14px', borderRadius:8, background:'var(--bg-card)', border:'1px solid var(--border)', fontSize:'0.82rem' }}>
            FOT: {stats.totals?.fot||0}
          </div>
          <div style={{ padding:'8px 14px', borderRadius:8, background:'var(--bg-card)', border:'1px solid var(--border)', fontSize:'0.82rem' }}>
            90D Focus: {stats.totals?.focus_90d||0}
          </div>
          <div style={{ padding:'8px 14px', borderRadius:8, background:'var(--bg-card)', border:'1px solid var(--border)', fontSize:'0.82rem' }}>
            Avg LTV: <strong>{fmt(stats.totals?.avg_ltv)}</strong>
          </div>
          <div style={{ marginLeft:'auto', display:'flex', gap:8 }}>
            <button className="btn btn-secondary btn-sm" onClick={exportAccounts}>⬇ Export</button>
            <button className="btn btn-primary btn-sm"
              onClick={() => setShowAdd(typeFilter || 'Distributor')}>+ Add Account</button>
          </div>
        </div>
      )}

      {/* Type filter tabs */}
      <div style={{ display:'flex', gap:6, marginBottom:12, flexWrap:'wrap' }}>
        <button onClick={() => { setTypeFilter(''); setPage(1); }}
          style={{ padding:'5px 12px', borderRadius:99, fontSize:'0.78rem', fontWeight:500, border:'1px solid var(--border)', cursor:'pointer',
            background: !typeFilter ? '#403EC9' : 'transparent', color: !typeFilter ? '#fff' : 'var(--text-secondary)' }}>
          All ({stats?.totals?.total||0})
        </button>
        {TYPES.map(t => {
          const cfg = getTypeConfig(t);
          const count = stats?.by_type?.find(s => s.account_type === t)?.total || 0;
          return (
            <button key={t} onClick={() => { setTypeFilter(t); setPage(1); }}
              style={{ padding:'5px 12px', borderRadius:99, fontSize:'0.78rem', fontWeight:500, cursor:'pointer',
                border: `1px solid ${typeFilter===t ? cfg.color : 'var(--border)'}`,
                background: typeFilter===t ? cfg.bg : 'transparent',
                color: typeFilter===t ? cfg.color : 'var(--text-secondary)' }}>
              {cfg.icon} {t} {count > 0 ? `(${count})` : ''}
            </button>
          );
        })}
      </div>

      {/* Search + filters */}
      <div style={{ display:'flex', gap:8, marginBottom:12, flexWrap:'wrap' }}>
        <input className="form-control" placeholder="Search name, contact, district…"
          value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
          style={{ flex:1, minWidth:200 }} />
        <SearchSelect value={stateFilter} onChange={v=>{ setStateFilter(v); setDistrictFilter(''); setPage(1); }} options={states} placeholder="All States" style={{width:150}}/>
        <SearchSelect value={districtFilter} onChange={v=>{ setDistrictFilter(v); setPage(1); }} options={districts.map(d=>({value:d,label:d}))} placeholder="All Cities" style={{width:160}}/>
        <select className="form-control" style={{ width:120 }} value={priorityFilter}
          onChange={e => { setPriority(e.target.value); setPage(1); }}>
          <option value="">All Priority</option>
          <option value="A">Priority A</option>
          <option value="B">Priority B</option>
          <option value="C">Priority C</option>
        </select>
        <button className="btn btn-ghost btn-sm" onClick={load}>↻</button>
      </div>

      {/* Table */}
      <div className="card">
        <div className="card-body no-pad">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Account</th>
                  <th>Type</th>
                  <th>Territory</th>
                  <th>Priority</th>
                  <th>Focus Product</th>
                  <th>Journey Stage</th>
                  <th>LTV</th>
                  <th>Last Visit</th>
                  <th>FOT</th>
                  <th>Added By</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="10" style={{ textAlign:'center', padding:48, color:'var(--text-muted)' }}>Loading…</td></tr>
                ) : accounts.map(a => {
                  const cfg = getTypeConfig(a.account_type);
                  return (
                    <tr key={a.id}>
                      <td>
                        <div style={{ fontWeight:600, fontSize:'0.88rem' }}>
                          {a.name}
                          {a.status==='pending_verification'&&<span className="badge" style={{marginLeft:6,fontSize:'0.62rem',background:'#FFF3CD',color:'#856404',border:'1px solid #FFEAA7'}}>⏳ Unverified</span>}
                          {a.source==='conference'&&a.status!=='pending_verification'&&<span className="badge" style={{marginLeft:6,fontSize:'0.62rem',background:'#E8E7FF',color:'#5E5CE6'}}>Conference</span>}
                        </div>
                        <div style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>{a.district}{a.state ? ` · ${a.state}` : ''}</div>
                      </td>
                      <td>
                        <span style={{ fontSize:'0.72rem', padding:'2px 8px', borderRadius:99, background:cfg.bg, color:cfg.color, fontWeight:500, whiteSpace:'nowrap' }}>
                          {cfg.icon} {a.account_type}
                        </span>
                      </td>
                      <td style={{ fontSize:'0.82rem' }}>{a.territory_name || '—'}</td>
                      <td>
                        <span className={`badge badge-${(a.priority||'b').toLowerCase()}`}>{a.priority||'—'}</span>
                      </td>
                      <td style={{ fontSize:'0.78rem', color:'var(--text-secondary)', maxWidth:120, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {a.focus_product_1 || '—'}
                      </td>
                      <td>
                        <span style={{ fontSize:'0.72rem', padding:'2px 7px', borderRadius:99, background:'var(--bg)', color:'var(--text-secondary)', border:'1px solid var(--border)' }}>
                          {JOURNEY_LABELS[a.journey_stage] || a.journey_stage || '—'}
                        </span>
                      </td>
                      <td style={{ fontWeight:600, color:'#403EC9', fontSize:'0.85rem' }}>{fmt(a.ltv_annual)}</td>
                      <td style={{ fontSize:'0.75rem', color:'var(--text-muted)' }}>
                        {a.last_visit_date ? new Date(a.last_visit_date).toLocaleDateString('en-IN', { day:'numeric', month:'short' }) : '—'}
                      </td>
                      <td>
                        {a.fot_available && <span className="badge badge-success" style={{ fontSize:'0.65rem' }}>FOT</span>}
                      </td>
                      <td style={{ fontSize:'0.75rem', color:'var(--text-muted)', whiteSpace:'nowrap' }}>{a.created_by_name || '—'}</td>
                      <td style={{ display:'flex', gap:4, padding:'6px 8px' }}>
                        <button className="btn btn-ghost btn-sm" style={{ fontSize:'0.72rem', padding:'2px 8px' }}
                          onClick={() => setSelected(a)}>View</button>
                        <button className="btn btn-ghost btn-sm" style={{ fontSize:'0.72rem', padding:'2px 8px', color:'#403EC9' }}
                          onClick={() => setEdit(a)}>Edit</button>
                        <button className="btn btn-ghost btn-sm" style={{ fontSize:'0.72rem', padding:'2px 8px', color:'#059669' }}
                          onClick={e => { e.stopPropagation(); setLogActivityFor({ id: a.id, name: a.name }); }} title="Log Activity">📞</button>
                        <button className="btn btn-ghost btn-sm" style={{ fontSize:'0.72rem', padding:'2px 8px', color:'var(--danger)' }}
                          onClick={() => deleteAccount(a.id, a.name, load)}><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg></button>
                      </td>
                    </tr>
                  );
                })}
                {!loading && !accounts.length && (
                  <tr><td colSpan="10" style={{ textAlign:'center', padding:48, color:'var(--text-muted)' }}>No accounts found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Pagination */}
      {meta.total > meta.limit && (
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:12, fontSize:'0.82rem', color:'var(--text-secondary)' }}>
          <span>Showing {((page-1)*meta.limit)+1}–{Math.min(page*meta.limit,meta.total)} of {meta.total.toLocaleString()}</span>
          <div style={{ display:'flex', gap:6 }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setPage(p=>p-1)} disabled={page===1}>← Prev</button>
            <span style={{ padding:'4px 8px' }}>Page {page}</span>
            <button className="btn btn-secondary btn-sm" onClick={() => setPage(p=>p+1)} disabled={page*meta.limit>=meta.total}>Next →</button>
          </div>
        </div>
      )}

      {/* Modals */}
      {selectedAccount && <AccountDetailModal account={selectedAccount} onClose={() => setSelected(null)} onEdit={() => { setEdit(selectedAccount); setSelected(null); }} />}
      {editAccount && <AccountEditModal account={editAccount} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); api.get('/accounts/stats').then(r => setStats(r.data.data)).catch(()=>{}); }} />}
      {showAdd && <AddAccountModal defaultType={showAdd} prefill={_prefillAccount} onClose={() => setShowAdd(null)} onSaved={() => { setShowAdd(null); load(); }} />}
      {logActivityFor && <LogActivityModal entityType="account" entityId={logActivityFor.id} entityName={logActivityFor.name} onClose={() => setLogActivityFor(null)} onSaved={() => setLogActivityFor(null)} />}
    </AppLayout>
  );
}

// ─── Account Detail Modal ─────────────────────────────────────────
function AccountDetailModal({ account: a, onClose, onEdit }) {
  const [actRefreshKey, setActRefreshKey] = useState(0);
  const [showLogActivity, setShowLogActivity] = useState(false);
  const cfg = getTypeConfig(a.account_type);
  const initials = (a.name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  const typeFields = [];
  if (a.account_type === 'Distributor') {
    if (a.distributor_reach_count) typeFields.push({ label:'Clinics Served', value: a.distributor_reach_count });
    if (a.distributor_reach_pct) typeFields.push({ label:'Reach %', value: `${Math.round(a.distributor_reach_pct * 100)}%` });
  }
  if (a.account_type === 'KOL') {
    if (a.kol_h_index) typeFields.push({ label:'H-Index', value: a.kol_h_index });
    if (a.kol_referral_network) typeFields.push({ label:'Referral Network', value: `~${a.kol_referral_network} doctors` });
    if (a.kol_conference_influence) typeFields.push({ label:'Conferences/yr', value: a.kol_conference_influence });
  }
  if (a.account_type === 'Key Hospital') {
    if (a.hospital_bed_count) typeFields.push({ label:'Beds', value: a.hospital_bed_count });
    if (a.hospital_dept_head) typeFields.push({ label:'Dept Head', value: a.hospital_dept_head });
    if (a.coverage_model) typeFields.push({ label:'Coverage', value: a.coverage_model });
  }

  const PRIORITY_COLORS = { A:'#16A34A', B:'#D97706', C:'#6B7280' };
  const JOURNEY_COLORS = { converted:'#16A34A', recurring:'#2563EB', demo_done:'#7C3AED', prospect:'#6B7280' };
  const journeyLabel = JOURNEY_LABELS[a.journey_stage] || a.journey_stage;
  const journeyColor = JOURNEY_COLORS[a.journey_stage] || '#6B7280';

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.35)', zIndex:1000, backdropFilter:'blur(2px)' }} />
      {/* Drawer */}
      <div style={{ position:'fixed', top:0, right:0, bottom:0, width:500, maxWidth:'95vw', background:'#fff', zIndex:1001, overflowY:'auto', boxShadow:'-8px 0 40px rgba(0,0,0,0.15)', display:'flex', flexDirection:'column' }}>

        {/* Gradient header */}
        <div style={{ background:'linear-gradient(135deg, #403EC9 0%, #6b6ef9 100%)', padding:'24px 24px 20px', flexShrink:0 }}>
          <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:16 }}>
            <div style={{ display:'flex', alignItems:'center', gap:14 }}>
              <div style={{ width:52, height:52, borderRadius:'50%', background:'rgba(255,255,255,0.2)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.1rem', fontWeight:800, color:'#fff', flexShrink:0, border:'2px solid rgba(255,255,255,0.4)' }}>
                {cfg.icon || initials}
              </div>
              <div>
                <div style={{ color:'rgba(255,255,255,0.7)', fontSize:'0.68rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:3 }}>
                  {cfg.icon} {a.account_type}
                </div>
                <div style={{ color:'#fff', fontWeight:800, fontSize:'1.1rem', lineHeight:1.2 }}>{a.name}</div>
                {(a.district || a.normalized_state || a.state) && (
                  <div style={{ color:'rgba(255,255,255,0.75)', fontSize:'0.82rem', marginTop:2 }}>
                    📍 {[a.district, a.normalized_state || a.state].filter(Boolean).join(', ')}
                  </div>
                )}
              </div>
            </div>
            <button onClick={onClose} style={{ background:'rgba(255,255,255,0.15)', border:'none', borderRadius:8, width:32, height:32, cursor:'pointer', color:'#fff', fontSize:'1rem', display:'flex', alignItems:'center', justifyContent:'center' }}>✕</button>
          </div>
          {/* Status pills */}
          <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
            <span style={{ background:'rgba(255,255,255,0.25)', color:'#fff', borderRadius:20, padding:'3px 12px', fontSize:'0.72rem', fontWeight:700 }}>Priority {a.priority}</span>
            <span style={{ background: journeyColor, color:'#fff', borderRadius:20, padding:'3px 12px', fontSize:'0.72rem', fontWeight:700, textTransform:'capitalize' }}>{journeyLabel}</span>
            {a.fot_available && <span style={{ background:'rgba(34,197,94,0.85)', color:'#fff', borderRadius:20, padding:'3px 12px', fontSize:'0.72rem', fontWeight:700 }}>FOT ✓</span>}
            {a.focus_90d && <span style={{ background:'rgba(217,119,6,0.85)', color:'#fff', borderRadius:20, padding:'3px 12px', fontSize:'0.72rem', fontWeight:700 }}>90D Focus</span>}
            <span style={{ marginLeft:'auto', background:'rgba(255,255,255,0.2)', color:'#fff', borderRadius:20, padding:'3px 12px', fontSize:'0.72rem', fontWeight:700 }}>LTV {fmt(a.ltv_annual)}</span>
          </div>
        </div>

        {/* Body */}
        <div style={{ flex:1, padding:'20px 24px', display:'flex', flexDirection:'column', gap:14 }}>

          {/* Quick actions */}
          <div style={{ display:'flex', gap:8 }}>
            <button onClick={() => setShowLogActivity(true)} style={{ flex:1, padding:'8px 0', borderRadius:8, border:'1.5px solid #403EC9', color:'#403EC9', background:'#fff', cursor:'pointer', fontWeight:700, fontSize:'0.82rem' }}>📞 Log Activity</button>
            <button onClick={onEdit} style={{ flex:1, padding:'8px 0', borderRadius:8, border:'none', background:'linear-gradient(135deg,#403EC9,#6b6ef9)', color:'#fff', cursor:'pointer', fontWeight:700, fontSize:'0.82rem' }}>✏️ Edit Account</button>
          </div>

          {/* Contact card */}
          <div style={{ background:'#F8F9FF', borderRadius:10, padding:'14px 16px', border:'1px solid #E5E7EB' }}>
            <div style={{ fontSize:'0.68rem', fontWeight:700, color:'#403EC9', textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:10 }}>📇 Contact Details</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px 16px' }}>
              {a.contact_name && <div style={{ gridColumn:'1/-1' }}><div style={{ fontSize:'0.68rem', color:'#6B7280', marginBottom:2 }}>Contact Person</div><div style={{ fontWeight:600, fontSize:'0.88rem' }}>{a.contact_name}</div></div>}
              {a.contact_number && <div><div style={{ fontSize:'0.68rem', color:'#6B7280', marginBottom:2 }}>Phone</div><a href={`tel:${a.contact_number}`} style={{ color:'#403EC9', textDecoration:'none', fontWeight:600, fontSize:'0.88rem' }}>{a.contact_number}</a></div>}
              {a.contact_email && <div><div style={{ fontSize:'0.68rem', color:'#6B7280', marginBottom:2 }}>Email</div><a href={`mailto:${a.contact_email}`} style={{ color:'#403EC9', textDecoration:'none', fontWeight:600, fontSize:'0.88rem', wordBreak:'break-all' }}>{a.contact_email}</a></div>}
              {a.territory_name && <div><div style={{ fontSize:'0.68rem', color:'#6B7280', marginBottom:2 }}>Territory</div><div style={{ fontWeight:600, fontSize:'0.88rem' }}>{a.territory_name}</div></div>}
              {a.assigned_name && <div><div style={{ fontSize:'0.68rem', color:'#6B7280', marginBottom:2 }}>Rep</div><div style={{ fontWeight:600, fontSize:'0.88rem' }}>{a.assigned_name}</div></div>}
            </div>
          </div>

          {/* Sales Playbook */}
          {(a.focus_product_1 || a.focus_product_2 || a.growth_play || a.outcome_goal_90d) && (
            <div style={{ background:'#F5F3FF', borderRadius:10, padding:'14px 16px', border:'1px solid #DDD6FE' }}>
              <div style={{ fontSize:'0.68rem', fontWeight:700, color:'#7C3AED', textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:10 }}>📋 Sales Playbook</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px 16px' }}>
                {a.focus_product_1 && <div><div style={{ fontSize:'0.68rem', color:'#7C3AED', marginBottom:2 }}>Focus Product 1</div><div style={{ fontWeight:600, fontSize:'0.88rem', color:'#26215C' }}>{a.focus_product_1}</div></div>}
                {a.focus_product_2 && <div><div style={{ fontSize:'0.68rem', color:'#7C3AED', marginBottom:2 }}>Focus Product 2</div><div style={{ fontWeight:600, fontSize:'0.88rem', color:'#26215C' }}>{a.focus_product_2}</div></div>}
                {a.growth_play && <div style={{ gridColumn:'1/-1' }}><div style={{ fontSize:'0.68rem', color:'#7C3AED', marginBottom:2 }}>Growth Play</div><div style={{ fontWeight:500, fontSize:'0.88rem', color:'#26215C' }}>{a.growth_play}</div></div>}
                {a.outcome_goal_90d && <div style={{ gridColumn:'1/-1' }}><div style={{ fontSize:'0.68rem', color:'#7C3AED', marginBottom:2 }}>90D Outcome Goal</div><div style={{ fontWeight:500, fontSize:'0.88rem', color:'#26215C' }}>{a.outcome_goal_90d}</div></div>}
              </div>
            </div>
          )}

          {/* Type-specific fields */}
          {typeFields.length > 0 && (
            <div style={{ background:'#F8F9FF', borderRadius:10, padding:'14px 16px', border:'1px solid #E5E7EB' }}>
              <div style={{ fontSize:'0.68rem', fontWeight:700, color:'#403EC9', textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:10 }}>{cfg.icon} {a.account_type} Details</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px 16px' }}>
                {typeFields.map(f => (
                  <div key={f.label}><div style={{ fontSize:'0.68rem', color:'#6B7280', marginBottom:2 }}>{f.label}</div><div style={{ fontWeight:600, fontSize:'0.88rem' }}>{f.value}</div></div>
                ))}
              </div>
            </div>
          )}

          {/* Recent visits */}
          {a.recent_visits?.length > 0 && (
            <div style={{ background:'#F9FAFB', borderRadius:10, padding:'14px 16px', border:'1px solid #E5E7EB' }}>
              <div style={{ fontSize:'0.68rem', fontWeight:700, color:'#6B7280', textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:10 }}>🚗 Recent Visits</div>
              {a.recent_visits.slice(0, 4).map(v => (
                <div key={v.id} style={{ display:'flex', justifyContent:'space-between', padding:'5px 0', borderBottom:'1px solid #E5E7EB', fontSize:'0.78rem' }}>
                  <span style={{ color:'#6B7280' }}>{new Date(v.visit_date).toLocaleDateString('en-IN', { day:'numeric', month:'short' })}</span>
                  <span style={{ fontWeight:500 }}>{v.outcome || v.visit_type || '—'}</span>
                  <span style={{ color:'#9CA3AF' }}>{v.rep_name}</span>
                  {v.demo_done && <span style={{ color:'#16A34A', fontWeight:600 }}>Demo ✓</span>}
                </div>
              ))}
            </div>
          )}

          {/* Deals */}
          <div style={{ background:'#F9FAFB', borderRadius:10, padding:'14px 16px', border:'1px solid #E5E7EB' }}>
            <div style={{ fontSize:'0.68rem', fontWeight:700, color:'#7C3AED', textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:10 }}>🏷 Pipeline Deals</div>
            <EntityDeals entityType="account" entityId={a.id} />
          </div>

          {/* Activity Timeline */}
          <div style={{ background:'#F9FAFB', borderRadius:10, padding:'14px 16px', border:'1px solid #E5E7EB' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
              <div style={{ fontSize:'0.68rem', fontWeight:700, color:'#6B7280', textTransform:'uppercase', letterSpacing:'0.07em' }}>🕐 Activity Timeline</div>
              <button onClick={() => setShowLogActivity(true)} style={{ padding:'4px 12px', borderRadius:6, border:'1.5px solid #403EC9', color:'#403EC9', background:'#fff', cursor:'pointer', fontWeight:700, fontSize:'0.75rem' }}>+ Log</button>
            </div>
            <ActivityTimeline entityType="account" entityId={a.id} refreshKey={actRefreshKey} />
          </div>

          <div style={{ fontSize:'0.72rem', color:'#9CA3AF', textAlign:'center', paddingBottom:8 }}>
            #{ a.serial_no } · Added {a.created_at ? new Date(a.created_at).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' }) : '—'}
          </div>
        </div>
      </div>

      {showLogActivity && (
        <LogActivityModal entityType="account" entityId={a.id} entityName={a.name}
          onClose={() => setShowLogActivity(false)}
          onSaved={() => { setShowLogActivity(false); setActRefreshKey(k => k + 1); }} />
      )}
    </>
  );
}

// ─── Shared: Deals mini-list for doctor / account detail panels ───
const DEAL_STAGE_META = {
  prospect:       { label:'Prospect',       color:'#6B7280', bg:'#F3F4F6' },
  contacted:      { label:'Contacted',      color:'#1D4ED8', bg:'#DBEAFE' },
  demo_scheduled: { label:'Demo Scheduled', color:'#6D28D9', bg:'#EDE9FE' },
  demo_done:      { label:'Demo Done',      color:'#92400E', bg:'#FEF3C7' },
  evaluating:     { label:'Evaluating',     color:'#991B1B', bg:'#FEE2E2' },
  won:            { label:'Won',            color:'#065F46', bg:'#D1FAE5' },
  lost:           { label:'Lost',           color:'#374151', bg:'#F9FAFB' },
};
const fmtDealVal = v => { const n=Number(v||0); if(n>=100000) return '₹'+(n/100000).toFixed(1)+'L'; return '₹'+n.toLocaleString('en-IN'); };

export function EntityDeals({ entityType, entityId }) {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!entityId) return;
    const param = entityType === 'doctor' ? { doctor_id: entityId } : { account_id: entityId };
    api.get('/deals', { params: { ...param, limit: 10 } })
      .then(r => setDeals(r.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [entityType, entityId]);

  if (loading) return <div style={{ fontSize:'0.78rem', color:'var(--text-muted)', padding:'8px 0' }}>Loading deals…</div>;
  if (!deals.length) return <div style={{ fontSize:'0.78rem', color:'#9CA3AF', padding:'8px 0' }}>No deals linked yet.</div>;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
      {deals.map(d => {
        const sm = DEAL_STAGE_META[d.stage] || DEAL_STAGE_META.prospect;
        return (
          <div key={d.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 10px', background:'#fff', borderRadius:7, border:'1px solid var(--border)' }}>
            <span style={{ background:sm.bg, color:sm.color, borderRadius:99, padding:'1px 8px', fontSize:'0.68rem', fontWeight:700, whiteSpace:'nowrap' }}>{sm.label}</span>
            <span style={{ flex:1, fontSize:'0.8rem', fontWeight:500, color:'#111', minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{d.title}</span>
            <span style={{ fontSize:'0.8rem', fontWeight:700, color:'#7C3AED', whiteSpace:'nowrap' }}>{fmtDealVal(d.deal_value)}</span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Account Edit Modal ───────────────────────────────────────────
const STATE_TERRITORY_MAP = {'Gujarat':'West','Rajasthan':'West','Delhi':'North','Uttar Pradesh':'North','Punjab':'North','Madhya Pradesh':'Central','Chhattisgarh':'Central','Maharashtra':'Maharashtra & Institutional','Telangana':'South-East','Andhra Pradesh':'South-East','Tamil Nadu':'South-West','Kerala':'South-West'};

function AccountEditModal({ account, onClose, onSaved }) {
  const [form, setForm] = useState({
    name:            account.name || '',
    country:         account.country || 'India',
    district:        account.district || '',
    state:           account.state || '',
    territory_id:    account.territory_id || '',
    priority:        account.priority || 'B',
    journey_stage:   account.journey_stage || 'prospect',
    fot_available:   account.fot_available || false,
    focus_90d:       account.focus_90d || false,
    contact_number:  account.contact_number || '',
    contact_email:   account.contact_email || '',
    address:         account.address || '',
    zip_code:        account.zip_code || '',
    gstin:           account.gstin || '',
    speciality:      account.speciality || '',
    focus_product_1: account.focus_product_1 || '',
    focus_product_2: account.focus_product_2 || '',
    growth_play:     account.growth_play || '',
    outcome_goal_90d: account.outcome_goal_90d || '',
    ltv_annual_override: account.ltv_annual_override || '',
    coverage_model:  account.coverage_model || '',
    distributor_reach_count: account.distributor_reach_count || '',
    kol_h_index:     account.kol_h_index || '',
    kol_referral_network: account.kol_referral_network || '',
    hospital_bed_count: account.hospital_bed_count || '',
    hospital_dept_head: account.hospital_dept_head || '',
    notes:           account.notes || '',
  });
  const [saving, setSaving] = useState(false);
  const [editTerritories, setEditTerritories] = useState([]);
  const [editStates, setEditStates] = useState([]);
  const cfg = getTypeConfig(account.account_type);

  useEffect(() => {
    api.get('/territories').then(r => setEditTerritories(r.data.data || [])).catch(() => {});
    api.get('/master-data/state').then(r => setEditStates((r.data.data || []).map(s => s.value || s))).catch(() => {});
  }, []);

  const set = (k, v) => setForm(prev => ({...prev, [k]: v}));

  const save = async () => {
    if (form.gstin && !GSTIN_RE.test(form.gstin)) { toast.error('GSTIN is not a valid 15-character GSTIN'); return; }
    setSaving(true);
    try {
      await api.put(`/accounts/${account.id}`, form);
      toast.success(`${account.name} updated`);
      onSaved();
    } catch(err) { toast.error(err?.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }} onClick={onClose}>
      <div style={{ background:'var(--bg-card)', borderRadius:12, width:560, maxHeight:'90vh', overflow:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }} onClick={e=>e.stopPropagation()}>
        <div style={{ padding:'14px 20px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center', position:'sticky', top:0, background:'var(--bg-card)', zIndex:1 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <span style={{ fontSize:18 }}>{cfg.icon}</span>
            <div>
              <div style={{ fontWeight:700, fontSize:'0.95rem' }}>Edit Account</div>
              <div style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>{account.account_type} · {account.name}</div>
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div style={{ padding:20, display:'flex', flexDirection:'column', gap:14 }}>

          {/* Basic info */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div className="form-group">
              <label className="form-label">Name</label>
              <input className="form-control" value={form.name} onChange={e=>set('name',e.target.value)} />
            </div>
            <SpecialitySelect value={form.speciality} onChange={v=>set('speciality',v)} label="Speciality / Category" placeholder="Search or add speciality…" />
          </div>

          {/* Country */}
          <CountrySelector value={form.country} onChange={v => setForm(p => ({ ...p, country: v, district: '', state: '', territory_id: '' }))} />

          {/* Location & Territory */}
          {form.country === 'India' ? (
            <>
              <CityStateSelect
                city={form.district} state={form.state}
                onCityChange={v => set('district', v)}
                onStateChange={v => {
                  set('state', v);
                  const terrName = STATE_TERRITORY_MAP[v];
                  const terr = editTerritories.find(t => t.name === terrName);
                  if (terr) set('territory_id', terr.id);
                }}
                stateOptions={editStates}
              />
              <div className="form-group">
                <label className="form-label">Territory</label>
                <select className="form-control" value={form.territory_id} onChange={e => set('territory_id', e.target.value)}>
                  <option value="">— Not assigned</option>
                  {editTerritories.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>
            </>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label className="form-label">City</label>
                <input className="form-control" value={form.district} onChange={e => set('district', e.target.value)} placeholder="City" />
              </div>
              <div className="form-group">
                <label className="form-label">State / Province / Region</label>
                <input className="form-control" value={form.state} onChange={e => set('state', e.target.value)} placeholder="State / Province / Region" />
              </div>
            </div>
          )}

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
            <div className="form-group">
              <label className="form-label">Priority</label>
              <select className="form-control" value={form.priority} onChange={e=>set('priority',e.target.value)}>
                <option value="A">A — High</option>
                <option value="B">B — Medium</option>
                <option value="C">C — Low</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Journey Stage</label>
              <select className="form-control" value={form.journey_stage} onChange={e=>set('journey_stage',e.target.value)}>
                {JOURNEY_STAGES.map(s => <option key={s} value={s}>{JOURNEY_LABELS[s]}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Coverage Model</label>
              <select className="form-control" value={form.coverage_model} onChange={e=>set('coverage_model',e.target.value)}>
                <option value="">— Not set</option>
                <option value="Direct Field">Direct Field</option>
                <option value="Hybrid: Field + Distributor">Hybrid: Field + Distributor</option>
                <option value="Distributor Only">Distributor Only</option>
              </select>
            </div>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div className="form-group">
              <label className="form-label">Contact Number</label>
              <input className="form-control" value={form.contact_number} onChange={e=>set('contact_number',e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-control" value={form.contact_email} onChange={e=>set('contact_email',e.target.value)} />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Address</label>
            <input className="form-control" value={form.address} onChange={e=>set('address',e.target.value)} placeholder="Building / Street / Locality" />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div className="form-group">
              <label className="form-label">Zip / Pin Code</label>
              <input className="form-control" value={form.zip_code} onChange={e=>set('zip_code',e.target.value)} placeholder="e.g. 411001" />
            </div>
            <div className="form-group">
              <label className="form-label">GSTIN</label>
              <input className="form-control" value={form.gstin} onChange={e=>set('gstin',e.target.value.toUpperCase())} placeholder="27AAJCR9213N1ZS" maxLength={15} />
            </div>
          </div>

          {/* Playbook */}
          <div style={{ background:'#EEEDFE', borderRadius:8, padding:'12px 14px' }}>
            <div style={{ fontWeight:600, fontSize:'0.82rem', color:'#403EC9', marginBottom:10 }}>📋 Playbook</div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              <div className="form-group">
                <label className="form-label">Focus Product 1</label>
                <input className="form-control" value={form.focus_product_1} onChange={e=>set('focus_product_1',e.target.value)} placeholder="e.g. alveoFlow FOT" />
              </div>
              <div className="form-group">
                <label className="form-label">Focus Product 2</label>
                <input className="form-control" value={form.focus_product_2} onChange={e=>set('focus_product_2',e.target.value)} />
              </div>
              <div className="form-group" style={{ gridColumn:'1/-1' }}>
                <label className="form-label">Growth Play</label>
                <input className="form-control" value={form.growth_play} onChange={e=>set('growth_play',e.target.value)} placeholder="e.g. FOT upgrade, consumable expansion" />
              </div>
              <div className="form-group" style={{ gridColumn:'1/-1' }}>
                <label className="form-label">90-Day Outcome Goal</label>
                <input className="form-control" value={form.outcome_goal_90d} onChange={e=>set('outcome_goal_90d',e.target.value)} placeholder="e.g. First FOT demo done, trial prescribed" />
              </div>
            </div>
          </div>

          {/* LTV */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div className="form-group">
              <label className="form-label">LTV Override (₹)</label>
              <input className="form-control" type="number" min="0" value={form.ltv_annual_override} onChange={e=>set('ltv_annual_override',parseFloat(e.target.value)||0)} />
            </div>
            <div style={{ paddingTop:22 }}>
              <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:'0.82rem', cursor:'pointer' }}>
                <input type="checkbox" checked={form.fot_available} onChange={e=>set('fot_available',e.target.checked)} />
                FOT device installed
              </label>
              <label style={{ display:'flex', alignItems:'center', gap:8, fontSize:'0.82rem', cursor:'pointer', marginTop:6 }}>
                <input type="checkbox" checked={form.focus_90d} onChange={e=>set('focus_90d',e.target.checked)} />
                On 90D focus list
              </label>
            </div>
          </div>

          {/* Type-specific fields */}
          {account.account_type === 'Distributor' && (
            <div style={{ border:'1px solid var(--border)', borderRadius:8, padding:'12px 14px' }}>
              <div style={{ fontWeight:600, fontSize:'0.82rem', marginBottom:10 }}>🏭 Distributor Details</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <div className="form-group">
                  <label className="form-label">Clinics / Doctors Served</label>
                  <input className="form-control" type="number" min="0" value={form.distributor_reach_count} onChange={e=>set('distributor_reach_count',parseInt(e.target.value)||0)} />
                </div>
              </div>
            </div>
          )}

          {account.account_type === 'KOL' && (
            <div style={{ border:'1px solid var(--border)', borderRadius:8, padding:'12px 14px' }}>
              <div style={{ fontWeight:600, fontSize:'0.82rem', marginBottom:10 }}>🎓 KOL Details</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10 }}>
                <div className="form-group">
                  <label className="form-label">H-Index</label>
                  <input className="form-control" type="number" min="0" value={form.kol_h_index} onChange={e=>set('kol_h_index',parseInt(e.target.value)||0)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Referral Network</label>
                  <input className="form-control" type="number" min="0" value={form.kol_referral_network} onChange={e=>set('kol_referral_network',parseInt(e.target.value)||0)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Conferences/yr</label>
                  <input className="form-control" type="number" min="0" value={form.kol_conference_influence} onChange={e=>set('kol_conference_influence',parseInt(e.target.value)||0)} />
                </div>
              </div>
            </div>
          )}

          {account.account_type === 'Key Hospital' && (
            <div style={{ border:'1px solid var(--border)', borderRadius:8, padding:'12px 14px' }}>
              <div style={{ fontWeight:600, fontSize:'0.82rem', marginBottom:10 }}>🏥 Hospital Details</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <div className="form-group">
                  <label className="form-label">Bed Count</label>
                  <input className="form-control" type="number" min="0" value={form.hospital_bed_count} onChange={e=>set('hospital_bed_count',parseInt(e.target.value)||0)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Dept Head / Contact</label>
                  <input className="form-control" value={form.hospital_dept_head} onChange={e=>set('hospital_dept_head',e.target.value)} />
                </div>
              </div>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea className="form-control" rows={2} value={form.notes} onChange={e=>set('notes',e.target.value)} />
          </div>

          <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Add Account Modal ────────────────────────────────────────────
function AddAccountModal({ defaultType, onClose, onSaved, prefill, excludeTypes = [] }) {
  const initType = (defaultType && TYPE_CONFIG[defaultType] && !excludeTypes.includes(defaultType)) ? defaultType : (Object.keys(TYPE_CONFIG).find(t => !excludeTypes.includes(t)) || 'Hospital');
  const [form, setForm] = useState({
    account_type: initType,
    name: prefill?.name||'', priority:'B', journey_stage:'prospect',
    country: 'India',
    territory_id: prefill?.territory_id||'', district: prefill?.city||'', state: prefill?.state||'', speciality:'',
    contact_number: prefill?.phone||prefill?.mobile||prefill?.contact_number||'', contact_email:'',
    address:'', zip_code:'',
    focus_product_1:'', growth_play:'', outcome_goal_90d:'',
    ltv_annual_override:0,
    coverage_model:'', gstin:'',
    fot_available:false, focus_90d:false,
    beds:'', outlets:'', employee_count:'', influence_level:'', institution:'',
  });
  const [territories, setTerritories] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/territories').then(r => setTerritories(r.data.data || [])).catch(()=>{});
  }, []);
  // Once territories load, resolve territory_id from pre-filled state if not already set
  useEffect(() => {
    if (!territories.length || !form.state || form.territory_id) return;
    const terrName = STATE_TERRITORY_MAP[form.state];
    const terr = territories.find(t => t.name === terrName);
    if (terr) setForm(p => ({...p, territory_id: terr.id}));
  }, [territories]); // form.state/territory_id intentionally omitted — only re-run when territories load

  const set = (k,v) => setForm(p => ({...p,[k]:v}));
  const cfg = getTypeConfig(form.account_type);
  const t = form.account_type;

  const onStateChange = (v) => {
    const terrName = STATE_TERRITORY_MAP[v];
    const terr = territories.find(tr => tr.name === terrName);
    setForm(p => ({ ...p, state: v, territory_id: terr?.id || p.territory_id }));
  };

  const save = async () => {
    if (!form.name) { toast.error('Name is required'); return; }
    if (form.country === 'India' && !form.state) { toast.error('Please select a State — it determines the account\'s territory'); return; }
    if (form.gstin && !GSTIN_RE.test(form.gstin)) { toast.error('GSTIN is not a valid 15-character GSTIN'); return; }
    setSaving(true);
    try {
      await api.post('/accounts', form);
      toast.success(`${form.name} added`);
      onSaved();
    } catch(err) { toast.error(err?.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const inp = 'form-control';
  const grp = 'form-group';
  const lbl = 'form-label';
  const grid2 = { display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }} onClick={onClose}>
      <div style={{ background:'var(--bg-card)', borderRadius:12, width:580, maxHeight:'90vh', overflow:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }} onClick={e=>e.stopPropagation()}>
        <div style={{ padding:'14px 20px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ fontWeight:700, fontSize:'0.95rem' }}>{cfg.icon} Add New Account</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div style={{ padding:20, display:'flex', flexDirection:'column', gap:12 }}>

          {/* Account type */}
          <div className={grp}>
            <label className={lbl}>Account Type</label>
            <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
              {Object.keys(TYPE_CONFIG).filter(type => !excludeTypes.includes(type)).map(type => {
                const c = getTypeConfig(type);
                return (
                  <button key={type} onClick={() => set('account_type', type)}
                    style={{ padding:'5px 10px', borderRadius:6, fontSize:'0.78rem', fontWeight:500, cursor:'pointer',
                      border:`1px solid ${form.account_type===type ? c.color : 'var(--border)'}`,
                      background: form.account_type===type ? c.bg : 'transparent',
                      color: form.account_type===type ? c.color : 'var(--text-secondary)' }}>
                    {c.icon} {type}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Name */}
          <div className={grp}>
            <label className={lbl}>Name *</label>
            <input className={inp} value={form.name} onChange={e=>set('name',e.target.value)}
              placeholder={t==='Distributor' ? 'e.g. MedPlus Distribution Pvt Ltd' : t==='Key Hospital' ? 'e.g. Apollo Hospital Hyderabad' : t==='KOL' ? 'e.g. Dr Rajesh Sharma' : 'Account name'}
              autoFocus />
          </div>

          {/* Country */}
          <div className={grp}>
            <CountrySelector value={form.country} onChange={v => setForm(p => ({ ...p, country: v, district: '', state: '', territory_id: '' }))} />
          </div>

          {/* City + State → India: full CityStateSelect + territory; International: plain inputs */}
          {form.country === 'India' ? (
            <>
              <div className={grp} style={{ gridColumn: '1/-1' }}>
                <CityStateSelect
                  city={form.district}
                  state={form.state}
                  onCityChange={v => set('district', v)}
                  onStateChange={onStateChange}
                  stateRequired
                />
              </div>
              {STATE_TERRITORY_MAP[form.state] && (
                <div style={{ background:'var(--brand-purple-faint)', border:'1px solid var(--brand-purple)', borderRadius:'var(--radius-md)', padding:'10px 14px', marginBottom:4, fontSize:'0.82rem' }}>
                  🗺️ Will be assigned to <strong>{STATE_TERRITORY_MAP[form.state]}</strong> territory
                </div>
              )}
            </>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className={grp}>
                <label className={lbl}>City</label>
                <input className={inp} value={form.district} onChange={e => set('district', e.target.value)} placeholder="City" />
              </div>
              <div className={grp}>
                <label className={lbl}>State / Province / Region</label>
                <input className={inp} value={form.state} onChange={e => set('state', e.target.value)} placeholder="State / Province / Region" />
              </div>
            </div>
          )}

          <div style={grid2}>
            <div className={grp}>
              <label className={lbl}>Contact Number</label>
              <input className={inp} value={form.contact_number} onChange={e=>set('contact_number',e.target.value)} placeholder="+91 XXXXX XXXXX" />
            </div>
            <div className={grp}>
              <label className={lbl}>Contact Email</label>
              <input className={inp} type="email" value={form.contact_email||''} onChange={e=>set('contact_email',e.target.value)} placeholder="account@example.com" />
            </div>
          </div>

          <div className={grp}>
            <label className={lbl}>Address</label>
            <input className={inp} value={form.address||''} onChange={e=>set('address',e.target.value)} placeholder="Building / Street / Locality" />
          </div>
          <div style={grid2}>
            <div className={grp}>
              <label className={lbl}>Zip / Pin Code</label>
              <input className={inp} value={form.zip_code||''} onChange={e=>set('zip_code',e.target.value)} placeholder="e.g. 411001" />
            </div>
            <div className={grp}>
              <label className={lbl}>GSTIN</label>
              <input className={inp} value={form.gstin||''} onChange={e=>set('gstin',e.target.value.toUpperCase())} placeholder="27AAJCR9213N1ZS" maxLength={15} />
            </div>
          </div>

          <div style={grid2}>
            <div className={grp}>
              <label className={lbl}>Priority</label>
              <select className={inp} value={form.priority} onChange={e=>set('priority',e.target.value)}>
                <option value="A">A — High</option>
                <option value="B">B — Medium</option>
                <option value="C">C — Low</option>
              </select>
            </div>
            <div className={grp}>
              <label className={lbl}>Journey Stage</label>
              <select className={inp} value={form.journey_stage} onChange={e=>set('journey_stage',e.target.value)}>
                {JOURNEY_STAGES.map(s => <option key={s} value={s}>{JOURNEY_LABELS[s]}</option>)}
              </select>
            </div>
          </div>

          {/* ── Type-specific fields ── */}
          {t === 'Distributor' && (<>
            <div style={grid2}>
              <div className={grp}>
                <label className={lbl}>Coverage Model</label>
                <select className={inp} value={form.coverage_model} onChange={e=>set('coverage_model',e.target.value)}>
                  <option value="">— Not set —</option>
                  <option value="Direct Field">Direct Field</option>
                  <option value="Hybrid: Field + Distributor">Hybrid: Field + Distributor</option>
                  <option value="Distributor Only">Distributor Only</option>
                </select>
              </div>
              <div className={grp} />
            </div>
          </>)}

          {t === 'Key Hospital' && (<>
            <div style={grid2}>
              <div className={grp}>
                <label className={lbl}>Bed Count</label>
                <input className={inp} type="number" min="0" value={form.beds} onChange={e=>set('beds',e.target.value)} placeholder="e.g. 250" />
              </div>
              <div className={grp}>
                <label className={lbl}>FOT Available</label>
                <select className={inp} value={form.fot_available ? 'yes' : 'no'} onChange={e=>set('fot_available', e.target.value==='yes')}>
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </div>
            </div>
          </>)}

          {t === 'KOL' && (<>
            <div style={grid2}>
              <div className={grp}>
                <label className={lbl}>Speciality</label>
                <input className={inp} value={form.speciality} onChange={e=>set('speciality',e.target.value)} placeholder="e.g. Pulmonologist" />
              </div>
              <div className={grp}>
                <label className={lbl}>Influence Level</label>
                <select className={inp} value={form.influence_level} onChange={e=>set('influence_level',e.target.value)}>
                  <option value="">Select</option>
                  <option value="National">National</option>
                  <option value="Regional">Regional</option>
                  <option value="Local">Local</option>
                </select>
              </div>
            </div>
            <div className={grp}>
              <label className={lbl}>Institution / Hospital</label>
              <input className={inp} value={form.institution} onChange={e=>set('institution',e.target.value)} placeholder="e.g. AIIMS Delhi" />
            </div>
          </>)}

          {t === 'Diagnostic Lab' && (
            <div className={grp}>
              <label className={lbl}>Speciality / Tests Offered</label>
              <input className={inp} value={form.speciality} onChange={e=>set('speciality',e.target.value)} placeholder="e.g. Pulmonary Function, Spirometry" />
            </div>
          )}

          {t === 'Corporate / OHC' && (
            <div className={grp}>
              <label className={lbl}>Employee Count</label>
              <input className={inp} type="number" min="0" value={form.employee_count} onChange={e=>set('employee_count',e.target.value)} placeholder="e.g. 500" />
            </div>
          )}

          {t === 'Pharmacy Chain' && (
            <div className={grp}>
              <label className={lbl}>Number of Outlets</label>
              <input className={inp} type="number" min="0" value={form.outlets} onChange={e=>set('outlets',e.target.value)} placeholder="e.g. 12" />
            </div>
          )}

          {t === 'Academic / Research' && (
            <div className={grp}>
              <label className={lbl}>Institution Type</label>
              <select className={inp} value={form.institution} onChange={e=>set('institution',e.target.value)}>
                <option value="">Select</option>
                <option value="Medical College">Medical College</option>
                <option value="Research Institute">Research Institute</option>
                <option value="University Hospital">University Hospital</option>
                <option value="Other">Other</option>
              </select>
            </div>
          )}

          {t === 'Government / PSU' && (
            <div className={grp}>
              <label className={lbl}>Tender / Empanelment Status</label>
              <select className={inp} value={form.growth_play} onChange={e=>set('growth_play',e.target.value)}>
                <option value="">Select</option>
                <option value="Empanelled">Empanelled</option>
                <option value="Tender Pending">Tender Pending</option>
                <option value="Not Empanelled">Not Empanelled</option>
              </select>
            </div>
          )}

          {/* Common bottom fields */}
          <div className={grp}>
            <label className={lbl}>Focus Product</label>
            <input className={inp} value={form.focus_product_1} onChange={e=>set('focus_product_1',e.target.value)} placeholder="e.g. alveoFlow FOT" />
          </div>
          <div className={grp}>
            <label className={lbl}>Growth Play</label>
            <input className={inp} value={form.growth_play} onChange={e=>set('growth_play',e.target.value)} placeholder="e.g. New FOT installation, consumable expansion" />
          </div>

          <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:4 }}>
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Adding…' : 'Add Account'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Log Activity Modal ───────────────────────────────────────────
const ACTIVITY_ICONS = { call:'📞', email:'📧', whatsapp:'💬', meeting:'🤝', note:'📝', demo:'🎯' };
const OUTCOME_OPTIONS = {
  call:     ['interested','not_interested','callback','no_answer','busy'],
  meeting:  ['converted','follow_up','demo_scheduled','order_placed'],
  demo:     ['converted','follow_up','demo_scheduled','order_placed'],
  email:    [],
  whatsapp: [],
  note:     [],
};
const OUTCOME_LABELS = {
  interested:'Interested', not_interested:'Not Interested', callback:'Callback',
  no_answer:'No Answer', busy:'Busy', converted:'Converted', follow_up:'Follow-up',
  demo_scheduled:'Demo Scheduled', order_placed:'Order Placed',
};

export function LogActivityModal({ entityType, entityId, entityName, onClose, onSaved }) {
  const [form, setForm] = useState({ activity_type:'call', outcome:'', duration_mins:'', notes:'', next_followup_date:'' });
  const [saving, setSaving] = useState(false);
  const set = (k,v) => setForm(p => ({ ...p, [k]: v }));

  const outcomeOpts = OUTCOME_OPTIONS[form.activity_type] || [];
  const showDuration = ['call','meeting'].includes(form.activity_type);

  const save = async () => {
    setSaving(true);
    try {
      await api.post('/activities', {
        entity_type: entityType,
        entity_id: entityId,
        activity_type: form.activity_type,
        outcome: form.outcome || null,
        duration_mins: form.duration_mins ? parseInt(form.duration_mins) : null,
        notes: form.notes || null,
        next_followup_date: form.next_followup_date || null,
      });
      toast.success('Activity logged');
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to log activity');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:1100, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }} onClick={onClose}>
      <div style={{ background:'var(--bg-card)', borderRadius:12, width:480, maxHeight:'90vh', overflow:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }} onClick={e=>e.stopPropagation()}>
        <div style={{ padding:'14px 20px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div>
            <div style={{ fontWeight:700, fontSize:'0.95rem' }}>Log Activity</div>
            {entityName && <div style={{ fontSize:'0.75rem', color:'var(--text-secondary)', marginTop:2 }}>{entityName}</div>}
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div style={{ padding:20, display:'flex', flexDirection:'column', gap:14 }}>

          {/* Activity type pills */}
          <div>
            <div className="form-label" style={{ marginBottom:8 }}>Activity Type</div>
            <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
              {Object.keys(ACTIVITY_ICONS).map(type => (
                <button key={type} onClick={() => { set('activity_type', type); set('outcome', ''); }}
                  style={{ padding:'6px 14px', borderRadius:99, fontSize:'0.8rem', fontWeight:600, cursor:'pointer',
                    border: form.activity_type === type ? '2px solid var(--brand-purple)' : '1px solid var(--border)',
                    background: form.activity_type === type ? 'var(--brand-purple-faint)' : 'transparent',
                    color: form.activity_type === type ? 'var(--brand-purple)' : 'var(--text-secondary)' }}>
                  {ACTIVITY_ICONS[type]} {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Outcome */}
          {outcomeOpts.length > 0 && (
            <div className="form-group">
              <label className="form-label">Outcome</label>
              <select className="form-control" value={form.outcome} onChange={e => set('outcome', e.target.value)}>
                <option value="">— Select outcome</option>
                {outcomeOpts.map(o => <option key={o} value={o}>{OUTCOME_LABELS[o]}</option>)}
              </select>
            </div>
          )}

          {/* Duration */}
          {showDuration && (
            <div className="form-group">
              <label className="form-label">Duration (minutes)</label>
              <input className="form-control" type="number" min="1" max="999" value={form.duration_mins}
                onChange={e => set('duration_mins', e.target.value)} placeholder="e.g. 15" />
            </div>
          )}

          {/* Notes */}
          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea className="form-control" rows={3} value={form.notes}
              onChange={e => set('notes', e.target.value)} placeholder="What happened? Key points discussed…" />
          </div>

          {/* Next follow-up */}
          <div className="form-group">
            <label className="form-label">Next Follow-up Date <span style={{ fontWeight:400, color:'var(--text-muted)' }}>(optional)</span></label>
            <input className="form-control" type="date" value={form.next_followup_date}
              onChange={e => set('next_followup_date', e.target.value)} />
          </div>

          <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:4 }}>
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Log Activity'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Activity Timeline ────────────────────────────────────────────
function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-IN', { day:'numeric', month:'short' });
}

const OUTCOME_COLORS = {
  interested:'#16A34A', not_interested:'#DC2626', callback:'#D97706',
  no_answer:'#9CA3AF', busy:'#9CA3AF', converted:'#7C3AED',
  follow_up:'#2563EB', demo_scheduled:'#059669', order_placed:'#B45309',
};

export function ActivityTimeline({ entityType, entityId, refreshKey }) {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!entityId) return;
    setLoading(true);
    // Use unified feed — shows visits + account_activities + bde_calls from all reps
    api.get('/activities/feed', { params: { entity_id: entityId, limit: 50 } })
      .then(r => setActivities(r.data.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [entityType, entityId, refreshKey]);

  if (loading) return <div style={{ padding:'16px 0', textAlign:'center', color:'var(--text-muted)', fontSize:'0.82rem' }}>Loading activities…</div>;
  if (!activities.length) return (
    <div style={{ padding:'16px 0', textAlign:'center', color:'var(--text-muted)', fontSize:'0.82rem' }}>
      No activities yet. Use "Log Activity" to record calls, meetings or notes.
    </div>
  );

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
      {activities.map(a => {
        const isOverdue = a.next_followup_date && new Date(a.next_followup_date) < new Date();
        return (
          <div key={a.id} style={{ display:'flex', gap:10, padding:'10px 12px', background:'var(--bg)', borderRadius:8, border:'1px solid var(--border)' }}>
            <div style={{ fontSize:'1.2rem', flexShrink:0, marginTop:1 }}>{ACTIVITY_ICONS[a.activity_type] || '📋'}</div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap', marginBottom:4 }}>
                <span style={{ fontWeight:600, fontSize:'0.82rem', textTransform:'capitalize' }}>{a.activity_type}</span>
                {a.outcome && (
                  <span style={{ fontSize:'0.72rem', padding:'1px 8px', borderRadius:99, background: OUTCOME_COLORS[a.outcome] ? OUTCOME_COLORS[a.outcome]+'22' : 'var(--border)', color: OUTCOME_COLORS[a.outcome] || 'var(--text-secondary)', fontWeight:600 }}>
                    {OUTCOME_LABELS[a.outcome] || a.outcome}
                  </span>
                )}
                {a.duration_mins && <span style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>{a.duration_mins} min</span>}
              </div>
              {a.notes && <div style={{ fontSize:'0.82rem', color:'var(--text-secondary)', marginBottom:4 }}>{a.notes}</div>}
              {a.next_followup_date && (
                <div style={{ fontSize:'0.75rem', fontWeight:600, color: isOverdue ? '#DC2626' : '#2563EB', marginBottom:4 }}>
                  {isOverdue ? '⚠ Overdue follow-up: ' : '📅 Follow-up: '}
                  {new Date(a.next_followup_date).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })}
                </div>
              )}
              <div style={{ display:'flex', alignItems:'center', gap:6, flexWrap:'wrap' }}>
                <span style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>
                  {a.rep_name || a.created_by_name || 'You'}
                </span>
                {a.rep_role && (
                  <span style={{ fontSize:'0.68rem', padding:'1px 6px', borderRadius:99, background:'var(--border)', color:'var(--text-secondary)', fontWeight:600, textTransform:'uppercase' }}>
                    {a.rep_role.replace('_',' ')}
                  </span>
                )}
                {a.source === 'visit' && (
                  <span style={{ fontSize:'0.68rem', padding:'1px 6px', borderRadius:99, background:'#DBEAFE', color:'#1E40AF', fontWeight:600 }}>Field Visit</span>
                )}
                {a.source === 'bde_call' && (
                  <span style={{ fontSize:'0.68rem', padding:'1px 6px', borderRadius:99, background:'#FEF3C7', color:'#92400E', fontWeight:600 }}>Prospect Call</span>
                )}
                <span style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>· {timeAgo(a.created_at)}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export { AddAccountModal };
